// Which bar of the UNROLLED voicings sheet the band is playing.
//
// The unrolled sheet (`unrollChart`) is one literal pass of the form written
// out — or, while cycling, that pass repeated — with every bar's id made
// unique as `${writtenId}#${index}`. The band's playhead indexes its OWN
// playback form, and the two orders agree only while playback is not
// pre-expanded: a plain or cycled form is the same flatten the sheet was
// unrolled from, so the index IS the sheet's bar (and a cycled clone carries
// a fresh id that only its position can recover). A pre-expanded form — the
// head · solos · out itinerary, a detached coda at ×N, a written-out ending,
// a passage loop — is a different order entirely, so its index landed on an
// unrelated bar or ran past the sheet's end and lit nothing. There the band's
// written id says which bar, and the occurrence which copy of it. A bar the
// pass plays more often than the sheet writes it — a tag replaying the last
// bars — stays on its last copy, which is where the sheet ends too.

/** The written measure id an unrolled bar was expanded from. */
export function unrolledSourceId(id: string): string {
  const hash = id.lastIndexOf('#');
  return hash < 0 ? id : id.slice(0, hash);
}

export function unrolledPlayingId(
  unrolled: readonly { id: string }[],
  playing: {
    /** The band's playhead, an index into ITS playback form (−1 = idle). */
    index: number;
    /** The written id of the bar at that index. */
    measureId: string | null;
    /** Whether that playback form is pre-expanded (a different order). */
    preExpanded: boolean;
    /** How many times that bar has already sounded in the current pass. */
    occurrence: number;
  },
): string | null {
  if (playing.index < 0) return null;
  if (!playing.preExpanded) return unrolled[playing.index]?.id ?? null;
  if (playing.measureId === null) return null;
  const copies = unrolled.filter((bar) => unrolledSourceId(bar.id) === playing.measureId);
  if (copies.length === 0) return null;
  return copies[Math.min(Math.max(0, playing.occurrence), copies.length - 1)].id;
}
